import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * FilterCount — libellé « N filtres » d'un bouton de bascule de panneau.
 *
 * POURQUOI DU JS. Compter les critères renseignés est la seule pièce du panneau
 * de filtres qu'un rjBuild ne sait pas exprimer : un chemin `~~.x.length` rend
 * bien la taille d'UN tableau, mais reactive-json n'a pas d'arithmétique — il ne
 * peut donc pas ADDITIONNER plusieurs critères, ni choisir entre singulier et
 * pluriel. Tout le reste du panneau (bascule, masquage, champs) reste du YAML.
 *
 * Le composant ne connaît AUCUN filtre : il reçoit des valeurs déjà résolues et
 * n'en juge que le remplissage. Il sert donc n'importe quel panneau, de
 * n'importe quel widget.
 *
 * Est considéré comme renseigné : un tableau non vide, une chaîne non blanche,
 * un nombre, un booléen `true`. Sont vides : `null`, `undefined`, `''`, `[]`,
 * `false` et `{}` — un objet vide étant ce que rend un champ jamais touché.
 *
 * Usage YAML :
 *   - type: FilterCount
 *     values:
 *       - ~~.apiData.data.objectData.data.pipeline
 *       - ~~.apiData.data.objectData.data.months
 *     emptyLabel: "Filtres"      # aucun critère renseigné (défaut : « Filtres »)
 *     singular: "filtre"          # défaut
 *     plural: "filtres"           # défaut
 *
 * Rend du texte nu : c'est à l'appelant de fournir le bouton et son habillage
 * (recettes DS `btn btn-sm btn-ghost`), pour que le même compteur serve une
 * pastille, un onglet ou un intitulé de section.
 */
const isFilled = (v) => {
  if (v == null) return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'string') return v.trim() !== ''
  if (typeof v === 'boolean') return v
  if (typeof v === 'object') return Object.keys(v).length > 0
  return true
}

export const FilterCount = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const raw = resolve(props.values)
  const values = Array.isArray(raw) ? raw : [raw]
  const count = values.filter(isFilled).length

  if (count === 0) return <>{resolve(props.emptyLabel) ?? 'Filtres'}</>
  const word = count > 1
    ? (resolve(props.plural) ?? 'filtres')
    : (resolve(props.singular) ?? 'filtre')
  return <>{`${count} ${word}`}</>
}
